import { useState } from "react"

import { LayoutDashboard } from "@/components/icons"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { AppSidebar } from "@/components/layouts/app-layout/app-sidebar"
import { SidebarSearchButton } from "@/components/sidebar/sidebar-search-button"
import { Button } from "@/components/ui/button"
import { SidebarGroup, SidebarMenu, SidebarMenuItem } from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useRoutes } from "@/contexts/use-routes"
import { cn } from "@/lib/utils"
import { useDashStore } from "@/stores/dash-store"

import { DashboardsFolder } from "./dashboards-folder"

export function DashSidebar() {
  const { state, isMobile } = useSidebar()
  const { routes } = useRoutes()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const sessionType = (() => {
    const matchedRoute = routes
      .filter((route) => pathname.startsWith(route.path))
      .sort((a, b) => b.path.length - a.path.length)
      .at(0)

    const resolveRouteSessionType = (routeId?: string, overrideSessionType?: unknown) => {
      if (typeof overrideSessionType === "string" && overrideSessionType.trim()) {
        return overrideSessionType
      }
      return routeId ?? "free"
    }

    if (matchedRoute) {
      return resolveRouteSessionType(matchedRoute.id, matchedRoute.overrides?.overrideSessionType)
    }

    const defaultExplorerRoute = routes.find((route) => route.component === "free")
    if (defaultExplorerRoute) {
      return resolveRouteSessionType(
        defaultExplorerRoute.id,
        defaultExplorerRoute.overrides?.overrideSessionType
      )
    }

    return routes[0]?.id ?? "free"
  })()

  const [globalSearch, setGlobalSearch] = useState("")

  const currentRoute = routes.find((r) => r.id === sessionType)

  if (!currentRoute) {
    return null
  }

  const explorerStore = useDashStore("dash")
  const requestNewDashboard = explorerStore((s) => s.requestNewDashboard)

  const handleNewDashboard = () => {
    requestNewDashboard()
    void navigate("/dash", { replace: true })
  }

  const isCollapsed = state === "collapsed" && !isMobile

  return (
    <AppSidebar>
      <SidebarGroup className="bg-sidebar sticky top-0 z-40 gap-2 p-2 pb-0">
        <SidebarMenu>
          <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={t("dash.landing.create_new")}
                  className={cn(
                    "w-full justify-start gap-2",
                    isCollapsed && "size-8 justify-center p-0"
                  )}
                  size="sm"
                  variant="outline"
                  onClick={handleNewDashboard}
                >
                  <LayoutDashboard className="size-4" />
                  {!isCollapsed && t("dash.landing.create_new")}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("dash.landing.create_new")}</TooltipContent>
            </Tooltip>
          </SidebarMenuItem>
        </SidebarMenu>
        {!isCollapsed && (
          <SidebarSearchButton searchValue={globalSearch} onSearchChange={setGlobalSearch} />
        )}
      </SidebarGroup>

      <SidebarGroup
        className={cn("flex flex-col gap-2 p-2", isCollapsed ? "items-center pt-1" : "")}
      >
        <DashboardsFolder globalSearchTerm={globalSearch} />
      </SidebarGroup>
    </AppSidebar>
  )
}
